import React, { useCallback, useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { isArray, isEqual, sortBy } from 'lodash';
import { useClickAway } from 'react-use'; // Option component

export const Option = ({
  children,
  className
}) => /*#__PURE__*/React.createElement("div", {
  className: className ? className : 'select__option'
}, children);
export const Select = ({
  children,
  emptyText,
  errors,
  helperText,
  initialValue,
  inputRef,
  isMultiple,
  isSearchable,
  label,
  name,
  small,
  onChange,
  placeholderText,
  searchValues,
  sortOptions,
  sortTags,
  values
}) => {
  // UI state
  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false); // values state

  const [selectValues, setSelectValues] = useState();
  const [filterValues, setFilterValues] = useState();
  const [originalValues, setOriginalValues] = useState();
  const [highlightValue, setHighlightValue] = useState();
  const [initialObject, setInitialObject] = useState(); // input refs [valueRefs are an array]

  const selectRef = useRef(null);
  const inputFieldRef = useRef(null);
  const valueRefs = useRef([]); // utility functions

  const utils = {
    loadValues: () => {
      let loadedValues = [];

      if (values) {
        loadedValues = values.map(v => typeof v === 'string' ? {
          value: v,
          label: v
        } : v);
      } else {
        loadedValues = children.map(c => ({
          value: c.props.value,
          label: c.props.children
        }));
      }

      if (sortOptions) loadedValues = sortBy(loadedValues, v => v.label);
      return loadedValues;
    },
    updateHighlight: newHighlightIndex => {
      if (typeof newHighlightIndex === 'undefined') {
        setHighlightValue(undefined);
      } else {
        setHighlightValue(filterValues[newHighlightIndex]);

        if (valueRefs.current[newHighlightIndex]) {
          valueRefs.current[newHighlightIndex].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }
      }
    },
    incrementHighlight: () => {
      let newHighlightIndex = 0;

      if (highlightValue) {
        const highlightIndex = filterValues.indexOf(highlightValue);

        if (highlightIndex >= 0) {
          newHighlightIndex = Math.min(highlightIndex + 1, filterValues.length - 1);
        }
      }

      utils.updateHighlight(newHighlightIndex);
    },
    decrementHighlight: () => {
      let newHighlightIndex = 0;

      if (highlightValue) {
        const highlightIndex = filterValues.indexOf(highlightValue);

        if (highlightIndex >= 0) {
          newHighlightIndex = Math.max(highlightIndex - 1, 0);
        }
      }

      utils.updateHighlight(newHighlightIndex);
    },
    resetInput: () => {
      if (inputFieldRef.current) {
        inputFieldRef.current.value = isMultiple && selectValues && selectValues.size > 0 ? '' : selectValues && selectValues.label || initialObject && initialObject.label || placeholderText;
      }
    },
    resetFilter: () => {
      setIsOpen(false);
      setIsFocused(false);

      if (inputFieldRef.current) {
        utils.resetInput();
        inputFieldRef.current.blur();
      }

      setFilterValues(originalValues);
      setHighlightValue(!isMultiple && selectValues ? selectValues : undefined);
    }
  }; // keypress handler callback

  const handleKeyPress = useCallback(event => {
    const {
      key
    } = event;

    switch (key) {
      case 'ArrowDown':
        !isOpen ? setIsOpen(true) : utils.incrementHighlight();
        break;

      case 'ArrowUp':
        !isOpen ? setIsOpen(true) : utils.decrementHighlight();
        break;

      case 'Escape':
      case 'Tab':
        if (isOpen) utils.resetFilter();
        break;

      case 'Enter':
        if (isOpen) {
          event.preventDefault();

          if (!isMultiple && !isEqual(selectValues, highlightValue) || isMultiple && !selectValues.has(highlightValue)) {
            handlers.handleChange(highlightValue);
          } else if (isMultiple) {
            handlers.onDeleteMultiple(highlightValue);
          }
        }

        break;

      default:
        break;
    }
  }, [isOpen, highlightValue, selectValues, utils]); // load initial values

  useEffect(() => {
    const loadedValues = utils.loadValues();
    setOriginalValues(loadedValues);
    setFilterValues(loadedValues);

    if (isMultiple) {
      if (initialValue) {
        let valueSet = isArray(initialValue) ? new Set(loadedValues.filter(v => initialValue.includes(v.value))) : new Set([loadedValues.find(v => v.value === initialValue)]);
        if (sortTags) valueSet = new Set(sortBy(Array.from(valueSet), v => v.label));
        setSelectValues(valueSet);
        if (onChange) onChange(Array.from(valueSet).map(v => v.value));
      } else {
        setSelectValues(new Set());
        if (onChange) onChange();
      }
    } else {
      const newInitialObj = initialValue ? loadedValues.find(v => v.value === initialValue) : {
        value: undefined,
        label: undefined
      };
      setInitialObject(newInitialObj);
      setSelectValues(newInitialObj);
      setHighlightValue(newInitialObj);
      if (onChange && newInitialObj) onChange(newInitialObj.value);
    }
  }, []); // add / remove key press handlers

  useEffect(() => {
    if (isFocused) {
      window.addEventListener('keydown', handleKeyPress);
      return () => {
        window.removeEventListener('keydown', handleKeyPress);
      };
    } else {
      window.removeEventListener('keydown', handleKeyPress);
    }
  }, [handleKeyPress, isFocused]); // event handler functions

  const handlers = {
    onFocus: () => {
      setIsFocused(true);

      if (inputFieldRef.current && inputFieldRef.current.value === placeholderText) {
        inputFieldRef.current.value = '';
      }
    },
    onBlur: () => {
      setIsFocused(false);
      setIsOpen(false);

      if (inputFieldRef.current && inputFieldRef.current.value.trim() === '') {
        utils.resetInput();
      }
    },
    onClick: () => {
      if (inputFieldRef.current) {
        inputFieldRef.current.focus();
      }

      children || values ? setIsOpen(!isOpen) : setIsOpen(false);
    },
    onDeleteMultiple: value => {
      selectValues.delete(value);
      if (onChange) onChange(Array.from(selectValues).map(v => v.value));
    },
    onOutsideClick: () => {
      utils.resetFilter();
    },
    handleChange: value => {
      if (value) {
        if (isMultiple) {
          let newValues = selectValues.add(value);
          if (sortTags) newValues = new Set(sortBy(Array.from(selectValues), v => v.label));
          setSelectValues(newValues);
          if (onChange) onChange(Array.from(selectValues).map(v => v.value));
          setIsOpen(true);
        } else {
          setSelectValues(value);
          setHighlightValue(value);
          setInitialObject(value);
          if (onChange) onChange(value.value);
          setIsOpen(false);
        }

        setFilterValues(originalValues);
        setIsFocused(true);

        if (inputFieldRef.current) {
          inputFieldRef.current.focus();
        }
      } else {
        utils.resetFilter();
      }
    },
    handleInputChange: event => {
      if (!isOpen) setIsOpen(true);
      const search = event.target.value.trim().toLowerCase();

      if (!search) {
        setFilterValues(originalValues);
        setHighlightValue(undefined);
      } else {
        const newFilterValues = originalValues.filter(v => v.label.toLowerCase().includes(search) || searchValues && v.value.toLowerCase().indexOf(search) >= 0);
        setFilterValues(newFilterValues);
        setHighlightValue(newFilterValues.length > 0 ? newFilterValues[0] : undefined);
      }
    }
  }; // click away hook used instead of onBlur for read-only input (!isSearchable)

  useClickAway(selectRef, handlers.onOutsideClick); // component body

  return /*#__PURE__*/React.createElement("div", {
    className: classnames('select', {
      select__active: isOpen,
      'select--sm': small
    }),
    ref: selectRef,
    tabIndex: "0"
  }, label && /*#__PURE__*/React.createElement("label", null, label), /*#__PURE__*/React.createElement("div", {
    name: name,
    className: classnames('select__header', {
      error: errors !== undefined,
      'select__header--focused': isFocused
    }),
    onClick: handlers.onClick,
    ref: inputRef
  }, selectValues && isMultiple && Array.from(selectValues).map((value, i) => /*#__PURE__*/React.createElement("span", {
    className: "select__value--tag",
    onClick: () => handlers.onDeleteMultiple(value),
    key: i
  }, value && value.label)), selectValues && !isSearchable && /*#__PURE__*/React.createElement("input", {
    className: "select__input select__input--readonly",
    key: selectValues instanceof Set ? selectValues.size : selectValues.label,
    onBlur: handlers.onBlur,
    onFocus: handlers.onFocus,
    readOnly: true,
    ref: inputFieldRef,
    tabIndex: "0",
    value: isMultiple && selectValues && selectValues.size > 0 ? '' : selectValues && selectValues.label || initialObject && initialObject.label || placeholderText
  }), selectValues && isSearchable && /*#__PURE__*/React.createElement("input", {
    autoComplete: "off",
    className: "select__input",
    defaultValue: isMultiple && selectValues && selectValues.size > 0 ? '' : selectValues && selectValues.label || initialObject && initialObject.label || placeholderText,
    key: selectValues instanceof Set ? selectValues.size : selectValues.label,
    onBlur: handlers.onBlur,
    onChange: handlers.handleInputChange,
    onFocus: handlers.onFocus,
    ref: inputFieldRef,
    tabIndex: "0"
  })), /*#__PURE__*/React.createElement("div", {
    className: classnames('select__options', {
      'select__options--active': isOpen,
      'select__options--multiple': isMultiple,
      'select__options--empty': filterValues && filterValues.length === 0
    })
  }, filterValues && filterValues.map((value, i) => /*#__PURE__*/React.createElement("div", {
    key: i,
    onMouseDown: !isMultiple && isEqual(selectValues, value) && undefined || isMultiple && selectValues.has(value) && (() => handlers.onDeleteMultiple(value)) || (() => handlers.handleChange(value))
  }, /*#__PURE__*/React.createElement("div", {
    // @todo AG: classnames not doing what i expect; going back to crazy ternery below
    // className={classnames('select__option', {
    //   'select__option--highlighted': isEqual(highlightValue, value),
    //   'select__option--selected': isMultiple && selectValues.has(value),
    // })}
    className: `select__option 
                  ${isEqual(highlightValue, value) ? 'select__option--highlighted' : isMultiple && selectValues.has(value) && 'select__option--selected'}`,
    value: value.value,
    ref: el => valueRefs.current[i] = el
  }, value.label))), filterValues && filterValues.length === 0 && /*#__PURE__*/React.createElement("div", {
    className: "select__option select__option--empty"
  }, emptyText)), errors && /*#__PURE__*/React.createElement("div", {
    className: "help-error"
  }, errors.message), helperText && /*#__PURE__*/React.createElement("div", {
    className: "help-text"
  }, helperText));
};
Select.defaultProps = {
  emptyText: 'not found',
  isMultiple: false,
  isSearchable: true,
  placeholderText: 'select...',
  searchValues: false,
  sortOptions: true,
  sortTags: true
};
Select.propTypes = {
  errors: PropTypes.object,
  helperText: PropTypes.string,
  isMultiple: PropTypes.bool,
  isSearchable: PropTypes.bool,
  label: PropTypes.string,
  name: PropTypes.string,
  onChange: PropTypes.func,
  values: PropTypes.array,
  children: PropTypes.array,
  emptyText: PropTypes.string,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  inputRef: PropTypes.func,
  placeholderText: PropTypes.string,
  searchValues: PropTypes.bool,
  sortOptions: PropTypes.bool,
  sortTags: PropTypes.bool
};